// Transport for the synth engine: parses a MIDI file, maps tracks to patches, schedules
// notes a little ahead of the AudioContext clock and reports the playhead so the piano
// roll can follow. One instance per MidiPlayer.
import { Midi } from '@tonejs/midi';
import { createEngine } from './engine';
import { describe } from './mapping';
import { preload } from './sampler';

const LOOKAHEAD = 0.6;     // seconds of notes scheduled ahead
const TICK_MS = 80;

let sharedCtx = null;
function audioContext() {
  if (!sharedCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    sharedCtx = new Ctx({ latencyHint: 'interactive' });
    window.__midimateAudio = sharedCtx;           // inspectable from devtools / the reel recorder
  }
  return sharedCtx;
}

// A context can report 'running' yet never advance (seen in headless Chromium and on some
// mobile browsers after an interruption). Resume it and make sure the clock moves; if it
// doesn't, throw the context away so the caller can start over with a fresh one.
async function ensureRunning(ctx) {
  if (ctx.state === 'suspended') await ctx.resume();
  const t0 = ctx.currentTime;
  await new Promise((r) => setTimeout(r, 250));
  if (ctx.currentTime > t0) return true;
  try { await ctx.close(); } catch (e) { /* already closed */ }
  if (sharedCtx === ctx) sharedCtx = null;
  return false;
}

export function parseMidi(arrayBuffer) {
  const midi = new Midi(arrayBuffer);
  const bpm = midi.header.tempos[0]?.bpm || 120;
  const tracks = midi.tracks
    .filter((t) => t.notes.length)
    .map((t) => ({
      name: t.name || '',
      channel: t.channel,
      program: t.instrument.number,
      isDrum: t.instrument.percussion,
      notes: t.notes.map((n) => ({ time: n.time, duration: n.duration, midi: n.midi, velocity: n.velocity })),
    }));
  const parts = describe(tracks, bpm);
  const style = parts[0]?.style || 'house';
  const events = [];
  for (const { track, role, patch, mods } of parts) {
    for (const n of track.notes) {
      events.push({ ...n, role, patch, mods, endTime: n.time + n.duration });
    }
  }
  events.sort((a, b) => a.time - b.time);
  const duration = events.reduce((m, e) => Math.max(m, e.endTime), 0);
  const hasKick = events.some((e) => e.role === 'drums' && (e.midi === 35 || e.midi === 36));
  return { bpm, style, parts, events, duration, hasKick };
}

export class SynthPlayer {
  constructor({ onTime, onActiveNote, onEnd, onStateChange } = {}) {
    this.onTime = onTime;
    this.onActiveNote = onActiveNote;
    this.onEnd = onEnd;
    this.onStateChange = onStateChange;
    this.song = null;
    this.engine = null;
    this.playing = false;
    this.offset = 0;          // song time when paused
    this.startAt = 0;         // ctx time that corresponds to song time 0 (while playing)
    this.timer = null;
    this.raf = null;
    this.nextEvent = 0;
    this.nextBeat = 0;
  }

  load(arrayBuffer) {
    this.pause();
    this.song = parseMidi(arrayBuffer);
    this.offset = 0;
    if (this.engine) { this.engine.dispose(); this.engine = null; }
    // Build the engine now so the sample pack can start downloading before play is pressed
    const ctx = audioContext();
    this.engine = createEngine(ctx, this.song.style);
    this.engine.setTempo(this.song.bpm);
    const patches = this.song.parts.filter((p) => p.role !== 'drums').map((p) => p.patch);
    const engine = this.engine;
    this.ready = preload(ctx, patches, engine.kit)
      .then((pack) => { if (pack && this.engine === engine) engine.setPack(pack); })
      .catch((e) => console.warn('Sample pack unavailable, using oscillators', e));
    return this.song;
  }

  get currentTime() {
    if (!this.song) return 0;
    if (!this.playing) return this.offset;
    return Math.min(this.engine.ctx.currentTime - this.startAt, this.song.duration);
  }

  get duration() { return this.song ? this.song.duration : 0; }

  async play() {
    if (!this.song || this.playing) return;
    let ctx = audioContext();
    if (!(await ensureRunning(ctx))) {
      // dead clock: rebuild the engine on a fresh context (the sample pack is cached by url)
      ctx = audioContext();
      await ensureRunning(ctx);
      this.engine?.dispose();
      this.engine = createEngine(ctx, this.song.style);
      this.engine.setTempo(this.song.bpm);
      const engine = this.engine;
      this.ready = preload(ctx, this.song.parts.filter((p) => p.role !== 'drums').map((p) => p.patch), engine.kit)
        .then((pack) => { if (pack && this.engine === engine) engine.setPack(pack); }).catch(() => {});
    }
    // Give the samples a moment to arrive; after that start anyway and they join when ready
    await Promise.race([this.ready, new Promise((r) => setTimeout(r, 5000))]);
    if (!this.song || this.playing) return;
    if (this.offset >= this.song.duration) this.offset = 0;
    this.startAt = ctx.currentTime + 0.08 - this.offset;
    const { events } = this.song;
    this.nextEvent = events.findIndex((e) => e.endTime > this.offset);
    if (this.nextEvent < 0) this.nextEvent = events.length;
    const beat = 60 / this.song.bpm;
    this.nextBeat = Math.ceil(this.offset / beat);
    this.playing = true;
    this.onStateChange?.(true);
    this.tick();
    this.timer = setInterval(() => this.tick(), TICK_MS);
    const frame = () => {
      if (!this.playing) return;
      const t = this.currentTime;
      this.onTime?.(t);
      this.onActiveNote?.(this.activeNoteAt(t));
      if (t >= this.song.duration) {
        this.pause();
        this.offset = 0;
        this.onTime?.(0);
        this.onActiveNote?.(null);
        this.onEnd?.();
        return;
      }
      this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }

  tick() {
    const { events, hasKick, bpm } = this.song;
    const songNow = this.engine.ctx.currentTime - this.startAt;
    const horizon = songNow + LOOKAHEAD;
    while (this.nextEvent < events.length && events[this.nextEvent].time < horizon) {
      const e = events[this.nextEvent++];
      // Notes already in progress at a seek start late and shortened
      const late = Math.max(0, songNow - e.time);
      if (late > 0 && (e.role === 'drums' || e.endTime <= songNow)) continue;
      const at = this.startAt + e.time + late;
      if (e.role === 'drums') this.engine.drum(e.midi, e.velocity, at);
      else this.engine.note(e.patch, e.midi, e.velocity, at, e.duration - late, e.mods);
    }
    if (!hasKick && this.engine.pumpOn) {
      const beat = 60 / bpm;
      while (this.nextBeat * beat < horizon && this.nextBeat * beat < this.song.duration) {
        this.engine.beat(this.startAt + this.nextBeat * beat);
        this.nextBeat++;
      }
    }
  }

  activeNoteAt(t) {
    // The visualizer paints the note passed plus everything held across it, so the most
    // recently started sounding note is enough
    const { events } = this.song;
    let best = null;
    for (let i = 0; i < events.length && events[i].time <= t; i++) {
      const e = events[i];
      if (e.endTime > t && (!best || e.time >= best.time)) best = e;
    }
    return best ? { startTime: best.time, endTime: best.endTime, pitch: best.midi } : null;
  }

  pause() {
    if (!this.playing) return;
    this.offset = this.currentTime;
    this.playing = false;
    clearInterval(this.timer);
    cancelAnimationFrame(this.raf);
    this.engine?.stopAll();
    this.onStateChange?.(false);
  }

  seek(t) {
    const wasPlaying = this.playing;
    this.pause();
    this.offset = Math.max(0, Math.min(t, this.duration));
    this.onTime?.(this.offset);
    this.onActiveNote?.(null);
    if (wasPlaying) this.play();
  }

  dispose() {
    this.pause();
    this.engine?.dispose();
    this.engine = null;
    this.song = null;
  }
}
